import { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../App';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '../components/ui/tabs';
import { FolderOpen, Upload, Link, FileText, Image, Music, Video, Trash2, Download, ExternalLink, Plus } from 'lucide-react';
import { toast } from 'sonner';

const API = process.env.REACT_APP_BACKEND_URL;

const KATEGORIJE = [
  { value: 'dokumenti', label: 'Dokumenti', icon: FileText },
  { value: 'slike', label: 'Slike', icon: Image },
  { value: 'zvok', label: 'Zvočni posnetki', icon: Music },
  { value: 'video', label: 'Video posnetki', icon: Video },
  { value: 'povezave', label: 'Povezave', icon: Link },
];

export default function FilesPage() {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('vse');
  const [uploadDialogOpen, setUploadDialogOpen] = useState(false);
  const [linkDialogOpen, setLinkDialogOpen] = useState(false);
  const [uploading, setUploading] = useState(false);
  
  // Form states
  const [selectedFile, setSelectedFile] = useState(null);
  const [naziv, setNaziv] = useState('');
  const [opis, setOpis] = useState('');
  const [kategorija, setKategorija] = useState('dokumenti');
  const [povezava, setPovezava] = useState('');
  
  const { user, token, hasRole } = useAuth();
  const canManage = hasRole(['predsednik', 'tajnik', 'gospodar']);
  
  const fetchFiles = useCallback(async () => {
    try {
      const params = activeTab !== 'vse' ? { kategorija: activeTab } : {};
      const response = await axios.get(`${API}/api/datoteke`, {
        headers: { Authorization: `Bearer ${token}` },
        params
      });
      setFiles(response.data);
    } catch (error) {
      console.error('Error fetching files:', error);
      toast.error('Napaka pri nalaganju datotek');
    } finally {
      setLoading(false);
    }
  }, [activeTab, token]);
  
  useEffect(() => {
    fetchFiles();
  }, [fetchFiles]);
  
  const handleFileUpload = async () => {
    if (!selectedFile || !naziv) {
      toast.error('Izberite datoteko in vnesite naziv');
      return;
    }
    
    setUploading(true);
    try {
      const formData = new FormData();
      formData.append('file', selectedFile);
      formData.append('naziv', naziv);
      formData.append('kategorija', kategorija);
      if (opis) formData.append('opis', opis);
      
      await axios.post(`${API}/api/datoteke/upload`, formData, {
        headers: {
          Authorization: `Bearer ${token}`,
          'Content-Type': 'multipart/form-data'
        }
      });
      
      toast.success('Datoteka naložena');
      setUploadDialogOpen(false);
      resetForm();
      fetchFiles();
    } catch (error) {
      console.error('Error uploading file:', error);
      toast.error(error.response?.data?.detail || 'Napaka pri nalaganju datoteke');
    } finally {
      setUploading(false);
    }
  };
  
  const handleAddLink = async () => {
    if (!naziv || !povezava) {
      toast.error('Vnesite naziv in povezavo');
      return;
    }
    
    setUploading(true);
    try {
      await axios.post(`${API}/api/datoteke`, {
        naziv,
        opis,
        kategorija: 'povezave',
        povezava
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      toast.success('Povezava dodana');
      setLinkDialogOpen(false);
      resetForm();
      fetchFiles();
    } catch (error) {
      console.error('Error adding link:', error);
      toast.error('Napaka pri dodajanju povezave');
    } finally {
      setUploading(false);
    }
  };
  
  const handleDelete = async (fileId) => {
    if (!window.confirm('Ali ste prepričani, da želite izbrisati to datoteko?')) return;
    
    try {
      await axios.delete(`${API}/api/datoteke/${fileId}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      toast.success('Datoteka izbrisana');
      fetchFiles();
    } catch (error) {
      console.error('Error deleting file:', error);
      toast.error('Napaka pri brisanju datoteke');
    }
  };
  
  const resetForm = () => {
    setSelectedFile(null);
    setNaziv('');
    setOpis('');
    setKategorija('dokumenti');
    setPovezava('');
  };
  
  const formatFileSize = (bytes) => {
    if (!bytes) return '';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };
  
  const formatDate = (dateStr) => {
    return new Date(dateStr).toLocaleDateString('sl-SI', {
      day: 'numeric',
      month: 'short',
      year: 'numeric'
    });
  };
  
  const getFileIcon = (file) => {
    const kat = KATEGORIJE.find(k => k.value === file.kategorija);
    const IconComponent = kat?.icon || FileText;
    return <IconComponent className="h-8 w-8" />;
  };
  
  const getFileUrl = (file) => {
    if (file.povezava) return file.povezava;
    if (file.pot_do_datoteke) return `${API}/api/datoteke/download/${file.pot_do_datoteke}`;
    return null;
  };
  
  const filteredFiles = activeTab === 'vse' 
    ? files 
    : files.filter(f => f.kategorija === activeTab);

  return (
    <div className="space-y-6" data-testid="files-page">
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">Datoteke</h1>
          <p className="text-gray-600">Dokumenti, slike, posnetki in povezave</p>
        </div>
        
        {canManage && (
          <div className="flex gap-2">
            <Dialog open={linkDialogOpen} onOpenChange={setLinkDialogOpen}>
              <DialogTrigger asChild>
                <Button variant="outline" data-testid="add-link-btn">
                  <Link className="h-4 w-4 mr-2" />
                  Dodaj povezavo
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Dodaj povezavo</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="text-sm font-medium">Naziv *</label>
                    <Input
                      value={naziv}
                      onChange={(e) => setNaziv(e.target.value)}
                      placeholder="Npr. Pravila lovske družine"
                      data-testid="link-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">URL povezava *</label>
                    <Input
                      value={povezava}
                      onChange={(e) => setPovezava(e.target.value)}
                      placeholder="https://drive.google.com/..."
                      data-testid="link-url-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Opis</label>
                    <Textarea
                      value={opis}
                      onChange={(e) => setOpis(e.target.value)}
                      placeholder="Kratek opis dokumenta..."
                      rows={2}
                    />
                  </div>
                  <Button 
                    onClick={handleAddLink} 
                    disabled={uploading}
                    className="w-full"
                    data-testid="save-link-btn"
                  >
                    {uploading ? 'Shranjujem...' : 'Shrani povezavo'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
            
            <Dialog open={uploadDialogOpen} onOpenChange={setUploadDialogOpen}>
              <DialogTrigger asChild>
                <Button data-testid="upload-file-btn">
                  <Upload className="h-4 w-4 mr-2" />
                  Naloži datoteko
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Naloži datoteko</DialogTitle>
                </DialogHeader>
                <div className="space-y-4 mt-4">
                  <div>
                    <label className="text-sm font-medium">Datoteka *</label>
                    <Input
                      type="file"
                      onChange={(e) => setSelectedFile(e.target.files[0])}
                      className="mt-1"
                      data-testid="file-input"
                    />
                    <p className="text-xs text-gray-500 mt-1">Največja velikost: 50MB</p>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Naziv *</label>
                    <Input
                      value={naziv}
                      onChange={(e) => setNaziv(e.target.value)}
                      placeholder="Naziv datoteke"
                      data-testid="file-name-input"
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium">Kategorija</label>
                    <Select value={kategorija} onValueChange={setKategorija}>
                      <SelectTrigger data-testid="category-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        {KATEGORIJE.filter(k => k.value !== 'povezave').map(kat => (
                          <SelectItem key={kat.value} value={kat.value}>
                            {kat.label}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div>
                    <label className="text-sm font-medium">Opis</label>
                    <Textarea
                      value={opis}
                      onChange={(e) => setOpis(e.target.value)}
                      placeholder="Kratek opis..."
                      rows={2}
                    />
                  </div>
                  <Button 
                    onClick={handleFileUpload} 
                    disabled={uploading}
                    className="w-full"
                    data-testid="upload-btn"
                  >
                    {uploading ? 'Nalagam...' : 'Naloži'}
                  </Button>
                </div>
              </DialogContent>
            </Dialog>
          </div>
        )}
      </div>
      
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList>
          <TabsTrigger value="vse" data-testid="tab-vse">
            <FolderOpen className="h-4 w-4 mr-2" />
            Vse
          </TabsTrigger>
          {KATEGORIJE.map(kat => (
            <TabsTrigger key={kat.value} value={kat.value} data-testid={`tab-${kat.value}`}>
              <kat.icon className="h-4 w-4 mr-2" />
              {kat.label}
            </TabsTrigger>
          ))}
        </TabsList>
        
        <TabsContent value={activeTab} className="mt-6">
          {loading ? (
            <div className="text-center py-12 text-gray-500">Nalagam...</div>
          ) : filteredFiles.length === 0 ? (
            <Card>
              <CardContent className="py-12 text-center text-gray-500">
                <FolderOpen className="h-12 w-12 mx-auto mb-4 opacity-50" />
                <p>Ni datotek v tej kategoriji</p>
                {canManage && (
                  <p className="text-sm mt-2">Kliknite "Naloži datoteko" ali "Dodaj povezavo" za dodajanje.</p>
                )}
              </CardContent>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
              {filteredFiles.map(file => (
                <Card key={file.id} className="hover:shadow-md transition-shadow" data-testid={`file-card-${file.id}`}>
                  <CardContent className="p-4">
                    <div className="flex items-start gap-3">
                      <div className={`p-2 rounded-lg ${
                        file.kategorija === 'slike' ? 'bg-purple-100 text-purple-600' :
                        file.kategorija === 'zvok' ? 'bg-orange-100 text-orange-600' :
                        file.kategorija === 'video' ? 'bg-red-100 text-red-600' :
                        file.kategorija === 'povezave' ? 'bg-blue-100 text-blue-600' :
                        'bg-gray-100 text-gray-600'
                      }`}>
                        {getFileIcon(file)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <h3 className="font-medium text-gray-900 truncate">{file.naziv}</h3>
                        {file.opis && (
                          <p className="text-sm text-gray-500 line-clamp-2 mt-1">{file.opis}</p>
                        )}
                        <div className="flex items-center gap-2 mt-2 text-xs text-gray-400">
                          <span>{formatDate(file.ustvarjeno)}</span>
                          {file.velikost && (
                            <>
                              <span>•</span>
                              <span>{formatFileSize(file.velikost)}</span>
                            </>
                          )}
                        </div>
                        <p className="text-xs text-gray-400 mt-1">
                          {file.avtor_ime}
                        </p>
                      </div>
                    </div>
                    
                    <div className="flex justify-end gap-2 mt-4 pt-3 border-t">
                      {getFileUrl(file) && (
                        file.povezava ? (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => window.open(file.povezava, '_blank')}
                            data-testid={`open-link-${file.id}`}
                          >
                            <ExternalLink className="h-4 w-4 mr-1" />
                            Odpri
                          </Button>
                        ) : (
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => {
                              window.open(`${API}/api/datoteke/download/${file.pot_do_datoteke}?token=${token}`, '_blank');
                            }}
                            data-testid={`download-${file.id}`}
                          >
                            <Download className="h-4 w-4 mr-1" />
                            Prenesi
                          </Button>
                        )
                      )}
                      {canManage && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                          onClick={() => handleDelete(file.id)}
                          data-testid={`delete-${file.id}`}
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      )}
                    </div>
                  </CardContent>
                </Card>
              ))}
            </div>
          )}
        </TabsContent>
      </Tabs>
    </div>
  );
}
